import { spentActions, type AttackVerb } from "./actionEconomy";
import type { Combatant, CombatantId } from "./combatant";
import type { ActivationState } from "./turn";

/**
 * Active **status instances** — their expiry vocabulary, the GM ad-hoc ruling a single
 * instance can carry, the name-reader accessors, and the canonical status-name constants.
 *
 * A status is an instance, not a bare name: it carries *which* status (`name`), *how long
 * it lasts* (`expiry`), and *who applied it* (`source`). Every named status the engine
 * keys on gets a constant here, so the resolution site, the seed overlay entry, and the
 * frontend can never drift onto differently-spelled strings.
 */

/**
 * The **who** axis of a `turn`-kind {@link StatusExpiry} — a status/area has two relevant
 * parties, the **carrier** it sits on (`"target"`) and whoever **applied** it
 * (`"source"`). Genuinely 2-valued rather than a boolean: expiry needs to distinguish
 * which of the two parties' turn triggers removal, not just whether one particular party
 * is active. Do not collapse this to a boolean. `"partner"` keys it to the instance's own
 * {@link StatusInstance.partner} instead: Mirage sits on the unit it hides and lasts until the end
 * of the invaded mech's next turn (`ms_neurospike`).
 */
export type StatusExpiryWho = "target" | "source" | "partner";

/**
 * The closed **expiry descriptor** for an active status instance or {@link AreaEffect}.
 * The duration bus (`@lancer-vtt/rules`' `durationBus.ts`) checks this at the matching
 * lifecycle moment and emits an eager-logged `status_remove`/`area_remove` step — the
 * descriptor *is* the state, no separate timer queue. The closed set:
 *   - `save_ends` — re-rolled as a save at the carrier's turn start (a fail keeps it).
 *     Deliberately NOT read by the duration bus (it has no `expiresAt` case for it) — the
 *     re-save lives on a separate rail, `@lancer-vtt/rules`' `saves.ts`. `stat` carries the
 *     HASE axis of the save that *caused* the status — RAW anchor `:8104`/`:8366` ("can
 *     successfully repeat **this save**", the axis of the originating forced save, not a
 *     property of the status itself). Absent `stat` (a bare `save_ends`, e.g. hand-authored
 *     or pre-migration data) falls back to the `+0` no-bonus roll (`:1090`) — never invent a
 *     default axis for it.
 *   - `end_of_round` — removed when the round ends.
 *   - `until_removed` — persists until explicitly cleared (the manual-tier default).
 *   - `scene` — lasts the whole encounter (cleared only at scene end).
 *   - `movement` — lasts through the carrier's next movement: removed when that movement ends
 *     (the whole movement, so a Covering Fire split ends at its rest), or at the end of the
 *     carrier's turn if no movement comes. Rapid Burst Jump Jets' "fly when you Boost".
 *   - `this_turn` — removed at the end of the turn in progress when it was applied, whoever's
 *     turn that is (the one `turn_end` whose `turnOrdinal` equals the stamped `appliedTurn`), or
 *     at round end if that turn never ends. Chain Axe's "until the end of the current turn".
 *   - `turn` — removed at the named `edge` (`"end"` | `"start"`) of the named party's
 *     ({@link StatusExpiryWho}) next turn. Covers Concussion Missiles' Impaired
 *     (`target`+`end`: applied during the attacker's turn, bites the carrier's next
 *     attack, wears off at the end of the carrier's next turn), a self-cast buff/cloud
 *     wearing off at the caster's own next turn end (`source`+`end`), Type-3 Projected
 *     Shield's "until the start of your next turn" (`source`+`start`), and
 *     `target`+`start`.
 *
 * **"Next" is genuinely next, never the turn the instance was applied on** — the duration
 * bus compares the lifecycle event's `turnOrdinal` against the carrier's stamped
 * {@link StatusInstance.appliedTurn}/{@link AreaEffect.appliedTurn} and skips the one
 * turn_end/turn_start that coincides with application.
 */
export type StatusExpiry =
  | { kind: "save_ends"; stat?: string }
  | { kind: "end_of_round" }
  | { kind: "until_removed" }
  | { kind: "scene" }
  | { kind: "movement" }
  | { kind: "this_turn" }
  | { kind: "turn"; edge: "start" | "end"; who: StatusExpiryWho }
  /**
   * Lasts while the carrier holds the named status, and ends the moment it doesn't (Markerlit and
   * the Marker Rifle Mark: "until the Lock On is cleared"). No lifecycle event ends it: its tie
   * listener (`@lancer-vtt/rules`' `statusTies.ts`) ends it when the carrier loses that status,
   * however it is lost.
   */
  | { kind: "while_status"; status: string };

/**
 * An **event that ends a status early** (`event-engine-lifecycle` D8), beside its {@link StatusExpiry}:
 * `damage`, the carrier takes damage; `move`, the carrier's movement ends. Active Camouflage: "Invisible
 * until you take damage, or until the end of your next turn" is `expiry` (the turn) plus
 * `endsOn: [{ on: "damage" }]`.
 */
export type EventEnd = { on: "damage" } | { on: "move" };

/**
 * A **GM-minted ad-hoc ruling** carried by a single {@link StatusInstance}. The GM grants
 * "+1 accuracy on your next attack, because that was a great idea with the smoke"
 * mid-encounter, in two clicks, without authoring a pack entry — and the grant is *real*:
 * `@lancer-vtt/rules`' pure `compileRuling(spec)` turns this record into an ordinary
 * `EffectEntry[]` at the one status-gather site, so it folds through the same engine every
 * authored status runs through.
 *
 * **A closed record of primitives, declared HERE in `@lancer-vtt/domain`, deliberately NOT
 * an inline effect body.** Two structural reasons:
 *
 * 1. **Dependency direction.** The effect AST (`ModifierContribution`, `EffectEntry`)
 *    lives in `@lancer-vtt/rules`, which depends on `@lancer-vtt/domain` — so an inline
 *    `mod`/`effects` field on `StatusInstance` would be a circular package dependency. A
 *    record of `number`/`string`/small unions needs nothing from `rules`.
 * 2. **Atoms, not scripting, enforced by the type rather than by policy.** The palette is
 *    a *form over the existing atom vocabulary*, never an expression field: a GM
 *    structurally cannot express anything `compileRuling` does not emit. Widening this
 *    toward a free-text expression/condition field is a design reversal — record it as a
 *    finding, don't bolt on a hatch.
 *
 * The omissions are the design. There is no field for a `kind:"action"` clause (statuses
 * never enumerate actions — the extra Quick action is the `grant_action_allowance`
 * *command*), none for an on-removal clause (`gatherHandlersForLostStatus` takes a bare
 * name *after* the instance is gone, so there is no spec left to read), and none for the
 * facets whose naive stacking is silently wrong (`overrideDamageType` is
 * last-writer-wins, `evasionCap` is a min not a sum, `missChance` is probabilistic).
 *
 * **No lifecycle:** the spec dies with the instance that carries it. Nothing to persist
 * separately, garbage-collect, or reconcile on reconnect.
 */
export type RulingSpec = {
  /**
   * The GM's stated reason — free text, rendered on the status chip's tooltip and the
   * COMMS activity line so the player can see *why* they have this. Required: a ruling
   * with no stated reason is the verbal ruling everyone forgets by round 3, which is the
   * thing this feature exists to replace.
   */
  reason: string;
  /**
   * Attacker-side accuracy this instance grants its carrier — positive for accuracy,
   * negative for difficulty. Compiles to a `ModifierContribution.attackerAccuracy`, which
   * `foldModifiers` sums additively and `dice.ts` then cancels 1:1 against difficulty and
   * rolls `|n|` d6 keeping the single highest — RAW-correct stacking for free.
   */
  accuracy?: number;
  /**
   * Bonus damage on the carrier's attack — dice, a flat amount, or both. Compiles to the
   * `extra_damage` **atom** (there is no damage facet on `ModifierContribution`), which is
   * also why an inline `mod` carrier could never have expressed this.
   *
   * There is deliberately no negative form: `extra_damage` has none, and the only
   * damage-*reduction* primitive in the engine (`reduce_pending_damage`) is reaction-window
   * only. A GM ruling that wants to take damage away lands as an advisory chip instead.
   */
  extraDamage?: { dice?: { count: number; sides: number }; amount?: number };
  /**
   * Narrow every grant in this spec to one attack verb ({@link AttackVerb}: `"skirmish"`,
   * `"ram"`, `"grapple"`, …) instead of any attack. Compiles to the emitted modifier's
   * `attackScope`. Omitted means the ruling applies to any attack the carrier makes. There is
   * no `"barrage"`: a Barrage shot presents as `"skirmish"` (see `ATTACK_VERBS`).
   */
  verb?: AttackVerb;
  /**
   * Self-remove when the carrier next *resolves an attack* — hit or miss. Compiles to a
   * `triggered`/`attack` clause matched at the **declared** phase, i.e. before the to-hit
   * roll, so the ruling burns on the attempt the way a GM means "your next attack." The
   * bonus still lands on that same attack (`resolve()` is pure — the modifier fold reads
   * unmutated state while the removal commits as a recorded step).
   *
   * Two behaviours worth knowing at the table rather than discovering in play: a *rejected*
   * or needs-decision attempt discards its declared steps entirely, so a ruling is **not**
   * burned by an attack that never happened; and a **reaction** attack routes through
   * `resolve()` too, so it **does** consume the ruling.
   */
  consumeOnAttack?: boolean;
  /**
   * How many times this ruling has been stacked onto its status (`grant_ruling`'s `stack`), so
   * "Rattled 2" is one chip rather than two. Absent means 1. `compileRuling` multiplies the
   * accuracy and bonus damage by it, and a `consumeOnAttack` burn removes the whole stack.
   */
  stacks?: number;
};

/**
 * An **active status instance** on a combatant: *which* status (`name`, a free string)
 * plus *how long it lasts* (`expiry`) and *who applied it* (`source`).
 *
 * Names stay deduped and order-insensitive *by name* — the reducer's `apply_status`
 * rejects a duplicate name. `source` is optional (a GM-applied status has no combatant
 * source).
 */
export type StatusInstance = {
  /** The status/condition name (free string — e.g. "Impaired", "Lock On"). */
  name: string;
  /** How long this instance lasts before the duration bus removes it. */
  expiry: StatusExpiry;
  /** The combatant that applied it (what a `source`-keyed expiry resolves against); omitted if GM-applied. */
  source?: CombatantId;
  /**
   * The **paired combatant** this instance's relationship points at — e.g. each side of a
   * mutual Grapple bind carries a `Grappled` marker whose `partner` is the *other*
   * combatant. **Additive/optional**, and deliberately distinct from {@link source}:
   * `source` is auto-stamped by `add_status` as the *acting* combatant (so both grapplers'
   * markers would point at the same side) and also drives `source`-keyed expiry —
   * cross-using it for a mutual pairing would corrupt that semantic. `partner` carries no
   * expiry meaning; it is a plain relationship pointer a status-specific consumer (the
   * Grapple break) reads directly. Omitted on every status that isn't a paired
   * relationship.
   */
  partner?: CombatantId;
  /**
   * The **triggering weapon instance key** a reservation-status is pinned to — the
   * `weaponInstanceKey` of the weapon that scored the hit which planted the reservation
   * (Executioner's "Backswing Cut Ready": "another melee attack with the SAME weapon").
   * **Additive/optional**, and the STRUCTURAL discriminator of a follow-up reservation:
   * only the `reserve_followup` atom stamps it, so a status carrying `weapon` IS a
   * reservation, and the room-gate matches a follow-up `request_attack` against it by
   * `weapon`-instance equality (the "same weapon" constraint) without needing the
   * reservation's name. It carries no expiry meaning. Omitted on every status that isn't a
   * weapon-pinned follow-up reservation.
   */
  weapon?: string;
  /**
   * The instance's **origin tag** — additive, closed to `"tech"`. Stamped on a `status_add`
   * emitted by either tech spine (a tech attack's resolution context — INVADE's FRAGMENT
   * SIGNAL; a tech activate — LOCK ON/BOLSTER) so **Shut Down** can end "tech-caused
   * statuses" by provenance, not by name: Impaired arrives BOTH from FRAGMENT SIGNAL (tech)
   * and the structure table's Glancing Blow (not tech), and only the former should clear.
   * Omitted on every non-tech status add (a GM manual apply, a structure/heat table effect,
   * Grapple, Brace, …) — absence reads as "not tech-caused", never a default that needs
   * invalidating.
   */
  cause?: "tech";
  /**
   * **Expiry-timer suspend.** A flag on the instance, not a bus-level hold: the duration
   * bus already iterates per-instance, so gating expiry per-instance is a one-line early
   * return there, whereas a bus-level hold would need a SECOND structure (keyed by
   * combatant + status name) duplicating what already lives on the instance. `true` means
   * this instance's expiry timer is frozen — `expiresAt` returns `false` unconditionally
   * regardless of which lifecycle event fires, and the instance resumes ticking normally
   * (expiring at the next matching lifecycle event) the instant it is unsuspended.
   * Omitted/`false` is the default; set and cleared by the `status_suspend_set` step.
   */
  suspended?: boolean;
  /**
   * The **application turn** — {@link ActivationState.turnOrdinal}'s live value at the
   * moment this instance was created. Only meaningful when {@link expiry} is the `turn`
   * kind: the duration bus's `expiresAt` skips the one turn_end/turn_start lifecycle event
   * whose `turnOrdinal` equals this value — the exact turn the instance was applied on — so
   * "until the end of your next turn" means genuinely next. Optional: every other expiry
   * kind omits it, there being nothing for it to gate.
   *
   * **Never auto-defaulted at apply time — an emitter that wants the fix live stamps it
   * explicitly**, exactly like `source`/`partner`/`cause`. {@link makeStatusInstance}, the one
   * constructor, carries it verbatim and says why. {@link AreaEffect.appliedTurn} sidesteps
   * the question because every one of its creation sites always stamps it; a status has many
   * creation sites, so it stays optional and explicit-only.
   */
  appliedTurn?: number;
  /**
   * The events that end this instance early, whatever its {@link expiry} says ({@link EventEnd}).
   * Omitted on every status that only its duration ends.
   */
  endsOn?: EventEnd[];
  /**
   * The **GM-minted ad-hoc ruling** this instance carries — see {@link RulingSpec}. Set
   * only by the GM-only `grant_ruling` command; omitted on every authored/engine status,
   * whose mechanics come from the pack overlay instead. When present it *replaces* the
   * overlay lookup at the one status-gather site, so a ruling-carrying status needs no pack
   * entry to be mechanically real.
   */
  ruling?: RulingSpec;
}

/**
 * What {@link makeStatusInstance} takes: a {@link StatusInstance} whose `expiry` may be
 * omitted. Keyed off `StatusInstance` itself, so a new facet on the type is a new accepted
 * key here with no edit. A caller may pass a wider object (a whole command or step, with its
 * `status` renamed to `name`); only the `StatusInstance` keys are read.
 */
export type StatusInstanceInit = Omit<StatusInstance, "expiry"> & { expiry?: StatusExpiry };

/**
 * Build the {@link StatusInstance} a status add persists — the ONE constructor every writer
 * converges on: the GM's `apply_status` command, the resolved `status_add` step, and
 * `grant_ruling`. Each of those also replays an inverted removal on undo, so a facet this
 * builder dropped would vanish from every undone removal with no type error and no crash.
 *
 * Pure, and deliberately validates nothing — each caller keeps its own guards, and they
 * legitimately differ (mirrors {@link import("./combatant").withDisclosureLevel}): the
 * `apply_status` command rejects a duplicate name and an unknown combatant, the `status_add`
 * step no-ops on a gone target or an already-present name, and `grant_ruling` replaces a
 * same-named instance. Co-effects (Flying-grounds) stay at the
 * call sites too.
 *
 * `expiry` defaults to `until_removed`. **Every other facet is carried verbatim and spread
 * only when defined, and none is ever defaulted** — `appliedTurn` above all
 * (`decisions/0132`). The same construction replays an inverted removal on undo, and an
 * instance that was never stamped (a directly-seeded fixture, say) is indistinguishable here
 * from a fresh emitter that simply omitted it. Defaulting either to the live `turnOrdinal`
 * would stamp a value the original instance never carried and break undo/redo equality. An
 * emitter that wants the next-turn fix live stamps `appliedTurn` itself at emission.
 *
 * The return type is inferred, not annotated, so {@link _EveryStatusInstanceFacetBuilt} can
 * read the spread list through it.
 */
export function makeStatusInstance(init: StatusInstanceInit) {
  return {
    name: init.name,
    expiry: init.expiry ?? { kind: "until_removed" as const },
    ...(init.appliedTurn !== undefined ? { appliedTurn: init.appliedTurn } : {}),
    ...(init.source !== undefined ? { source: init.source } : {}),
    ...(init.partner !== undefined ? { partner: init.partner } : {}),
    ...(init.weapon !== undefined ? { weapon: init.weapon } : {}),
    ...(init.cause !== undefined ? { cause: init.cause } : {}),
    ...(init.suspended !== undefined ? { suspended: init.suspended } : {}),
    ...(init.ruling !== undefined ? { ruling: init.ruling } : {}),
    ...(init.endsOn !== undefined ? { endsOn: init.endsOn } : {}),
  } satisfies StatusInstance;
}

/**
 * The fixed RAW duration of a status whose rules text sets one no matter who applies it. A GM's
 * `apply_status` that names no `expiry` takes this, so a hand-applied Braced ends when a braced
 * one would (game night 2026-09-23: it never expired). Only intrinsic durations belong here: a
 * status whose duration depends on what caused it (Impaired, Slowed, Shredded) or on a source
 * unit (a mark that ends on its applier's turn) has no default and stays `until_removed`.
 */
const RAW_STATUS_EXPIRY: Readonly<Record<string, { expiry: StatusExpiry; nextTurn?: true }>> = {
  // Brace (Core, "Reactions"): "until the end of your next turn, all other attacks against you are made at +1 Difficulty".
  braced: { expiry: { kind: "turn", edge: "end", who: "target" }, nextTurn: true },
  // Everest Hyperspec Fuel Injector, Power Up: "For the rest of this scene".
  "power up": { expiry: { kind: "scene" } },
};

/** The status the Brace reaction lands on the bracer (the `Braced` overlay entry's id). */
export const BRACED_STATUS = "Braced";

/**
 * Is `c` taking its **braced turn** right now? RAW Core p.73, Brace: "you cannot take reactions
 * until the end of your next turn and on that turn, you can only take one quick action – you cannot
 * OVERCHARGE, move normally, take full actions, or take free actions."
 *
 * True iff `c` is the acting unit and carries a `Braced` applied before this turn. `Braced` expires
 * at the end of the bracer's next turn, so on any turn of its own it is either that turn or the turn
 * it Braced in (an Overwatch shot during its own move), which the `appliedTurn` stamp tells apart.
 * An unstamped instance counts, as `has_status`'s `appliedBefore` reads one.
 */
export function onBracedTurn(c: Pick<Combatant, "id" | "statuses">, activation: Pick<ActivationState, "turn" | "turnOrdinal">): boolean {
  const { turn } = activation;
  if (turn.phase !== "acting" || turn.actor !== c.id) return false;
  return c.statuses.some((s) => s.name === BRACED_STATUS && (s.appliedTurn === undefined || s.appliedTurn !== activation.turnOrdinal));
}

/** What a braced turn refuses ({@link bracedTurnLimit}): a Full action, a free action (Overcharge, a Protocol), or a second Quick. */
export type BracedTurnLimit = "no_full" | "no_free" | "quick_taken";

/**
 * Which of Brace's next-turn limits refuses `action` for `c` now, or `undefined` when it is legal or
 * this is not `c`'s {@link onBracedTurn braced turn}. The one reading behind the server wall
 * (`checkBudget`) and the command menu's greyed rows, so the two can't disagree. A Quick counts as
 * taken once the turn has spent an action point or logged any action use.
 */
export function bracedTurnLimit(
  c: Pick<Combatant, "id" | "statuses">,
  activation: Pick<ActivationState, "turn" | "turnOrdinal">,
  action: "quick" | "full" | "free",
): BracedTurnLimit | undefined {
  const { turn } = activation;
  if (turn.phase !== "acting" || !onBracedTurn(c, activation)) return undefined;
  if (action === "free") return "no_free";
  if (action === "full") return "no_full";
  const acted = spentActions(turn.budget) > 0 || Object.values(turn.budget.actionUses ?? {}).some((n) => n > 0);
  return acted ? "quick_taken" : undefined;
}

/**
 * {@link RAW_STATUS_EXPIRY}'s default for `name` (case-insensitive), if the status has one. A
 * "your next turn" duration is stamped with `turnOrdinal`, the live turn, so it means the carrier's
 * NEXT turn even when applied during its own (the Brace reaction stamps the same way).
 */
export function rawStatusDefaults(name: string, turnOrdinal: number): { expiry: StatusExpiry; appliedTurn?: number } | undefined {
  const entry = RAW_STATUS_EXPIRY[name.trim().toLowerCase()];
  if (entry === undefined) return undefined;
  return { expiry: entry.expiry, ...(entry.nextTurn ? { appliedTurn: turnOrdinal } : {}) };
}

/** Errors unless `T` is `never` — the carrier for the pin just below. */
type AssertNever<T extends never> = T;

/**
 * **Compile-time exhaustiveness pin over {@link makeStatusInstance}'s spread list**, the
 * constructor-side twin of `steps.ts`'s `_EveryStatusFacetCarriedFromInstance`. Keyed on
 * `keyof StatusInstance` (the SOURCE key set), so adding a facet to the type fails
 * `bun run typecheck` until the builder carries it; keyed through `ReturnType`, so deleting a
 * spread line fails it too. Unlike the step-layer pins there is no uncarried complement: the
 * builder carries every facet, `suspended` included, and the step caller simply has none to
 * pass. Witnessed both ways: a throwaway `pinWitness?: string` on `StatusInstance` gives
 * `TS2344: Type '"pinWitness"' does not satisfy the constraint 'never'`; deleting the `weapon`
 * spread gives `TS2344: Type '"weapon"' does not satisfy the constraint 'never'`.
 */
type _EveryStatusInstanceFacetBuilt = AssertNever<
  Exclude<keyof StatusInstance, keyof ReturnType<typeof makeStatusInstance>>
>;

// The status math (resolver, CLI view) reads names; the duration machinery reads
// instances. These thin helpers keep every name-reader off the instance shape so
// the conversion from `string[]` is centralized.

/** The status **names** carried by a combatant's instance list, in order. */
export function statusNames(statuses: readonly StatusInstance[]): string[] {
  return statuses.map((s) => s.name);
}

/** Whether a combatant carries a status by name (exact match — the dedup key). */
export function hasStatus(statuses: readonly StatusInstance[], name: string): boolean {
  return statuses.some((s) => s.name === name);
}

/**
 * **The Open-door predicate** — `true` iff `c` is authored as a door
 * (`base.objectProfile?.door`) **and** currently carries the `"Open"` marker status. Every
 * seam that treats an open door as fully passable/see-through reads THIS predicate, never
 * a bare `hasStatus(c.statuses, "Open")`: the door-marker containment lives inside the
 * predicate itself, so a free-text "Open" status applied to a plain wall (or anything
 * else) can never make it passable. Exported — unlike each seam's own local kind
 * predicates — because getting this containment right in exactly one place matters more
 * here than the seams' independent-evolution discipline.
 */
export function isOpenDoor(c: Combatant): boolean {
  return c.base.objectProfile?.door === true && hasStatus(c.statuses, "Open");
}

/**
 * Whether a combatant is **functionally Stunned** — carries `"Stunned"` OR `"Shut Down"`.
 * Shut Down is one composite status that never adds a literal `"Stunned"` instance (see
 * {@link SHUT_DOWN_STATUS}), so a plain `hasStatus(c.statuses, "Stunned")` reads `false` on
 * a shut-down mech even though RAW treats it as Stunned indefinitely. Any hard "can't act"
 * gate must read THIS predicate, never the raw name.
 */
export function isStunned(combatant: Pick<Combatant, "statuses">): boolean {
  return hasStatus(combatant.statuses, "Stunned") || hasStatus(combatant.statuses, SHUT_DOWN_STATUS);
}

/**
 * Whether a combatant is **functionally Slowed** — carries `"Slowed"` OR `"Prone"` (RAW
 * Core p.77: "PRONE characters are SLOWED") OR `"Towing"` (dragging is Slowed, {@link TOWING_STATUS}). A READ-ONLY predicate over the carried
 * statuses, never a state mutation.
 *
 * **Do NOT "fix" a Prone-but-not-Slowed carrier by auto-adding a literal `Slowed`
 * instance** — `status_add`/`status_remove` are idempotent by name, so a `save_ends` Slowed
 * layered onto an already-Prone carrier would either no-op (if a Slowed instance already
 * exists) or, worse, a later save-off of that literal instance would un-Slow a carrier that
 * is still Prone. Same trap {@link isStunned} documents for Shut Down.
 */
export function isSlowed(combatant: Pick<Combatant, "statuses">): boolean {
  return hasStatus(combatant.statuses, SLOWED_STATUS) || hasStatus(combatant.statuses, PRONE_STATUS) || hasStatus(combatant.statuses, TOWING_STATUS);
}

/**
 * The canonical **Hidden** status name — the single string the Hidden targeting gate, the
 * seed overlay entry, and the frontend un-targetable set all key on, so they can never
 * drift. A HIDDEN combatant can't be the direct target of a unit-targeted attack (the
 * encounter-room legality gate hard-rejects it; the GM `gm_force` override and
 * area/cell attacks are unaffected).
 */
export const HIDDEN_STATUS = "Hidden";

/**
 * The canonical **Shut Down** status name — the single string the Shut Down resolution, the
 * Boot Up resolution, and the seed overlay entry all key on.
 *
 * Shut Down is deliberately **one composite status**, not a `Stunned` + `Shut Down` pair:
 * `status_add`/`status_remove` are idempotent BY NAME, so adding `Stunned` to an
 * already-Stunned (e.g. `save_ends`) mech would silently no-op, and that foreign instance
 * could later expire by save — un-Stunning a shut-down mech, a RAW violation ("nothing can
 * prevent this condition"). The composite's overlay entry spreads `STUNNED`'s effects by
 * object reference, so it inherits Stunned's real math automatically. See {@link isStunned},
 * the predicate every "can this combatant act" gate must key off instead of
 * `hasStatus(..., "Stunned")` alone.
 */
export const SHUT_DOWN_STATUS = "Shut Down";

/**
 * The canonical **Biological** status name — the single string the NPC spawn-attach
 * (`spawnNpc`, keyed on `class.role === "biological"`, case-insensitive) and the seed
 * overlay entry both key on. The overlay entry carries the tech-immunity scope (excepting
 * Scan and Lock On); this constant just keeps the spawn-attach and the overlay id from
 * drifting apart.
 */
export const BIOLOGICAL_STATUS = "Biological";

/**
 * The canonical **Jammed** condition name (RAW Core p.78) — the single string the
 * status-keyed action-legality wall (`@lancer-vtt/rules`' `economy.ts`) and the seed
 * overlay entry both key on.
 */
export const JAMMED_STATUS = "Jammed";

/**
 * The canonical **Slowed** condition name (RAW Core p.78) — keyed by the domain reducer's
 * `boost` case (Slowed forbids Boost) and the seed entry. See {@link isSlowed} for the
 * composite predicate that also folds in {@link PRONE_STATUS}.
 */
export const SLOWED_STATUS = "Slowed";

/**
 * The canonical **Prone** status name (RAW Core p.77: "PRONE characters are SLOWED"). Read
 * ONLY through {@link isSlowed} at any "is this carrier Slowed" gate; never derive a literal
 * `Slowed` instance from Prone's presence (see {@link isSlowed}).
 */
export const PRONE_STATUS = "Prone";

/**
 * The canonical **Immobilized** status name (RAW Core p.78: "IMMOBILIZED characters cannot
 * make any voluntary movements, although involuntary movements are unaffected"). The single
 * string `voluntaryMoveBar` (`../movement`) and the seed overlay entry both key on.
 * Involuntary movement (`forced_move`) bypasses `voluntaryMoveBar` by construction — it
 * folds `pos` directly in `applyResolved.ts`, never entering `move_token`/`mayPlayMove`.
 */
export const IMMOBILIZED_STATUS = "Immobilized";

/**
 * The canonical **Grappled** marker name — keyed by the domain reducer's `boost` case, the
 * status-keyed reaction/action-legality walls (Grappled forbids Boost and reactions), and
 * the seed entry.
 */
export const GRAPPLED_STATUS = "Grappled";

/**
 * The canonical **Invisible** status name (RAW Core p.77) — keyed by the seed entry's
 * `missChance` facet and by the Hide-cover relaxation (an Invisible hider needs no cover to
 * Hide).
 */
export const INVISIBLE_STATUS = "Invisible";

/**
 * The canonical **Prowling** status name — the Infiltrator talent's turn-scoped stealth
 * marker (RAW Prowl: "During your turn: entering line of sight of hostiles or moving from
 * cover does not stop you from being Hidden; you can pass freely through — but not end your
 * turn in — enemy spaces; you can Hide even in plain sight of enemies"). Granted at the
 * carrier's turn-start and cleared at its turn-end, it modulates three built-ins that are
 * otherwise status-blind: the mid-turn reveal sweep (which skips a Prowling carrier), the
 * Hide-action cover precondition, and the movement-lane occupant gate (which flips a hostile
 * occupant to pass-through for a Prowling mover).
 */
export const PROWLING_STATUS = "Prowling";

/**
 * The canonical **Ignore Difficult Terrain** status name — a mover carrying it pays 1 per space
 * where terrain or a passable object would charge more (`movementContext`'s `moveCost`). Generic
 * on purpose: Bulwark Mods ("allow you to ignore difficult terrain") grants it, and Juggernaut 3
 * reads the same. The Prone crawl's difficult-terrain floor still applies: Prone is a condition
 * of the mover, not of the ground.
 */
export const IGNORE_DIFFICULT_TERRAIN_STATUS = "Ignore Difficult Terrain";

/**
 * The canonical **Blinded** status name: its carrier has line of sight only to adjacent spaces
 * (the Flash Mine: "they only have line of sight to adjacent spaces until the end of their next
 * turn"). Not a RAW condition, a named status for that clause. Read where a line of sight is
 * drawn from a combatant: the attack and tech LoS wall, the area-origin LoS wall, and the
 * `line_of_sight` reads of the spatial answerer.
 */
export const BLINDED_STATUS = "Blinded";

/**
 * The canonical **Soft Cover** status name: its carrier has soft cover against ranged attacks (RAW
 * Core p.66, +1 Difficulty), whatever stands between it and the attacker. Content plants it for "you
 * gain soft cover" (the Veil Rifle's shroud, the Ace's Chaff Launchers). Read by the to-hit cover
 * fold (`attackModifiers.ts`), which floors the cover degree to soft rather than stacking it.
 */
export const SOFT_COVER_STATUS = "Soft Cover";

/**
 * The canonical **Grounded** status name: its carrier can't fly (the Bouncing Mine: "they can't fly
 * until the end of their next turn"). Content can't plant `Flying` on it (`add_status`'s wall in the
 * effects interpreter); a GM's direct apply is the override tier and still can.
 */
export const GROUNDED_STATUS = "Grounded";

/**
 * The canonical **Mesmerized** status name: the only voluntary movements its carrier can make are
 * toward the status's `source` (the Mesmer Beacon: "the only voluntary movements they can make are
 * toward you"). Not a RAW condition, a named status for that clause. `move_token` refuses a move
 * whose end is not closer to the source, or whose chosen path steps away from it.
 */
export const MESMERIZED_STATUS = "Mesmerized";

/**
 * The canonical **Cable Winch Attached** status name: its carrier and its `partner` (the winch's
 * carrier) "may not move more than {@link TETHER_RANGE} spaces away from each other". `move_token`
 * refuses either one a voluntary move that ends farther than that from the other.
 */
export const TETHERED_STATUS = "Cable Winch Attached";

/** The {@link TETHERED_STATUS} cable's length: "They are 5 spaces long". */
export const TETHER_RANGE = 5;

/**
 * The canonical **Towing** status name: its carrier is towing the other end of a cable winch
 * (`partner`), "obeying the normal rules for lifting and dragging, and becoming Slowed while doing
 * so" (`ms_cable_winch_system`; Core p.63, Lifting and Dragging: "Mechs can drag characters or
 * objects up to twice their SIZE but are SLOWED while doing so … While dragging or lifting,
 * characters can't take reactions"). {@link isSlowed} reads it as Slowed; the reaction wall refuses
 * its carrier; the partner follows the carrier's moves (`dragRecompute.ts`, through
 * `towedPartnerOf`).
 */
export const TOWING_STATUS = "Towing";

/**
 * The **Reeling** marker: its carrier is "unable to take reactions until the end of their next
 * turn" (`t_infiltrator:2`, Ambush). Not a RAW status name; the reaction wall
 * (`checkReactionStatusLegality`) refuses its carrier, and the clause that adds it sets the expiry.
 */
export const REELING_STATUS = "Reeling";

/**
 * The canonical **Down and Out** status name (RAW Core p.77) — a pilot at 0 HP is
 * "unconscious and Stunned". The status-keyed can't-act wall gates on this name ALONGSIDE
 * {@link isStunned}, never folded into that predicate, which is pinned to Stunned ∨ Shut
 * Down. Landed by the damage cascade's null-structure branch the first time a `pilot`-kind
 * combatant hits 0 HP (RAW: unconscious, not destroyed). Its seed overlay entry spreads
 * `STUNNED`'s effects by object reference — the same composite trick {@link
 * SHUT_DOWN_STATUS} uses — so the evasion cap / auto-fail-checks math is inherited for free.
 */
export const DOWN_AND_OUT_STATUS = "Down and Out";

/**
 * The canonical **Dead** marker status name — a SECOND marker landed beside the ordinary
 * `"Destroyed"` status when a pilot already carrying {@link DOWN_AND_OUT_STATUS} takes
 * further damage (RAW p.77: a Down and Out pilot who takes ANY more damage dies outright).
 * A plain, independently self-inverting `status_add`/`status_remove` pair alongside the
 * `destroy` step — purely informational, since every hard gate a dead pilot needs is already
 * covered by the `"Destroyed"` marker `destroy` lands, so it needs no bespoke fold logic.
 */
export const DEAD_STATUS = "Dead";

/**
 * The canonical **Cascading** status name (RAW Core p.71: "any time a mech with the AI tag
 * makes a structure damage or overheating check, roll 1d20. On a 1 the NHP cascades" — the
 * NHP "immediately takes over the mech and becomes an NPC under the control of the GM").
 * Landed by a natural-1 cascade check ridden inside the overheating/structure-damage
 * resolutions, cleared by Shut Down. Its seed overlay entry is **MARKER-ONLY** — no combat
 * modifier rides it; the "can only Shut Down while Cascading" enforcement is a status-aware
 * branch in `canResolveIntent`/`canIssueEncounter` (`../../permissions`), not anything here.
 */
export const CASCADING_STATUS = "Cascading";

/**
 * The canonical **COMP/CON Engaged** marker status name (RAW Core p.71: "give control of
 * your mech to its COMP/CON as a protocol, allowing your mech to act independently on your
 * turn with its own set of actions"). Landed by the `request_engage_compcon` intent once the
 * actor is proven to carry a cascade-immune AI system — an equipped system tagged BOTH
 * `tg_ai` and `tg_no_cascade`, re-derived at command time rather than frozen.
 *
 * **MARKER-ONLY** — no combat modifier rides it: it must NOT alter targeting, control, or
 * initiative. `expiry: "until_removed"`, because RAW attaches no scripted "disengage the
 * COMP/CON" reversal; the GM's manual `remove_status` is the only way to clear it outside
 * undo.
 */
export const COMPCON_ENGAGED_STATUS = "COMP/CON Engaged";

/**
 * The canonical **Unpiloted** marker status name — stamped on a mech by Dismount/Eject
 * (partnered via {@link StatusInstance.partner} to the spawned pilot combatant's id),
 * cleared by Mount. Rides the invertible `apply_status`/`remove_status` shape like every
 * other marker status, so it needs no bespoke inverse.
 *
 * **Unpiloted ≠ Shut Down** (RAW p.73: "a powered-OFF mech always has SHUT DOWN" —
 * dismounting doesn't power down): NO evasion cap, NO tech immunity, just excluded from
 * activation and from acting (the can't-act wall keys on THIS name directly, never folded
 * into {@link isStunned}, which is pinned to Stunned ∨ Shut Down). NHP/COMP-CON control of
 * an empty mech (RAW p.75) is GM-fiction — a named non-goal, nothing here models it.
 */
export const UNPILOTED_STATUS = "Unpiloted";

/**
 * The rider-side marker `act_jockey`'s successful attach lands (RAW Core p.75).
 * `@lancer-vtt/rules`' `jockey.ts` defines its own identical-string constant for its local
 * consumers; this domain-level copy exists because `classifyOccupant` (`../movement`, below
 * `rules` in the dependency graph) needs it: a jockeying rider and its carrier deliberately
 * SHARE a cell, and ordinary occupancy classification (a mech-scale occupant blocks a
 * smaller pilot mover) would otherwise trap the rider in place, unable to path away to "jump
 * off as part of your movement" (RAW).
 */
export const JOCKEYING_STATUS = "Jockeying";
/** The carrier-side marker `act_jockey`'s successful attach lands — see {@link JOCKEYING_STATUS}'s doc. */
export const JOCKEYED_STATUS = "Jockeyed";

/**
 * The rider-side marker a **consent-based** allied MOUNT lands (RAW Core p.70: "you can also
 * MOUNT or DISMOUNT willing allied mechs or vehicles. When you do so, move into the same
 * space and then move with them"). A sibling of {@link JOCKEYING_STATUS}, NOT a reuse of it:
 * the hostile Jockey menu (Distract/Shred/Damage) and the carrier's `request_shake_off` are
 * keyed to the `Jockeying`/`Jockeyed` pair specifically, so a `Riding`/`Ridden` pairing
 * structurally can't reach them and no per-consumer side-check is required. Shares the SAME
 * co-location plumbing as Jockey — `classifyOccupant`'s occupancy exemption and the
 * `jockeyRecomputer` both recognize either pair.
 */
export const RIDING_STATUS = "Riding";
/** The carrier-side marker a consent-based allied MOUNT lands — see {@link RIDING_STATUS}'s doc. */
export const RIDDEN_STATUS = "Ridden";

/**
 * The carrier-side marker of a **Black Thumb Rodeo** (`t_black_thumb:2`, Long Rim): the pilot
 * "partly climb[s] out", "occupies your mech's space and moves when it moves". `partner` is the
 * rider, a pilot combatant minted beside the mech that the mech never loses (no Unpiloted). A third
 * co-location pair beside Jockey and Riding, so it shares their occupancy exemption and drag.
 */
export const RODEO_STATUS = "Black Thumb Rodeo";
/** The rider-side marker of a Black Thumb Rodeo — see {@link RODEO_STATUS}'s doc. */
export const RODEO_RIDER_STATUS = "Rodeo Rider";
